import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { Add, Delete } from '@material-ui/icons'
import { TICKET_RULE, RULE_LABELS } from 'config/constants'
import useStyles from './TicketRules.style'

export const RuleForm = ({
  rule,
  events,
  pendingEvents,
  onRequestTicketsOfEvent,
  onChange,
  onValidChange,
  showError,
  onRemove,
  usedTypes = []
}) => {
  const classes = useStyles()
  const eventsInitializedRef = useRef(false)
  const ticketsInitializedRef = useRef(false)
  const onValidChangeRef = useRef(onValidChange)

  const [type, setType] = useState(
    rule?.Type ?? TICKET_RULE.BOUGHT_FROM_ANY_EVENT
  )
  const [selectedEvents, setSelectedEvents] = useState([])

  // Event selection modal state
  const [openEventModal, setOpenEventModal] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [modalTempSelected, setModalTempSelected] = useState([])

  // Type 3: ticket picker state
  const [ticketEventId, setTicketEventId] = useState('')
  const [localTickets, setLocalTickets] = useState([])
  const [pendingLocalTickets, setPendingLocalTickets] = useState(false)
  const [ticketSelected, setTicketSelected] = useState('')
  const [ticketNameSelected, setTicketNameSelected] = useState('')
  const [selectedTicketsList, setSelectedTicketsList] = useState([])

  // Type 3: Min / MaxPurchases
  const [min, setMin] = useState(rule?.Min ?? 1)
  const [maxPurchases, setMaxPurchases] = useState(rule?.MaxPurchases ?? '')

  // Initialize selectedEvents from rule.RequestedEventIds once events are loaded
  useEffect(() => {
    if (
      !eventsInitializedRef.current &&
      events?.length &&
      rule?.RequestedEventIds?.length
    ) {
      eventsInitializedRef.current = true
      setSelectedEvents(
        events.filter((e) => rule.RequestedEventIds.includes(e.Id))
      )
    }
  }, [events, rule])

  // Initialize selectedTicketsList from rule.RequestedTicketIds when editing a Type-3 rule
  useEffect(() => {
    if (
      ticketsInitializedRef.current ||
      type !== TICKET_RULE.BOUGHT_ANY_TICKET_FROM_EVENT ||
      !rule?.RequestedTicketIds?.length ||
      !selectedEvents.length ||
      !onRequestTicketsOfEvent
    ) {
      return
    }
    ticketsInitializedRef.current = true

    const fetchAll = async () => {
      const remaining = new Set(rule.RequestedTicketIds)
      const result = []

      for (const event of selectedEvents) {
        if (remaining.size === 0) break
        try {
          const tickets = await onRequestTicketsOfEvent(event.Id)
          if (Array.isArray(tickets)) {
            for (const t of tickets) {
              if (remaining.has(t.Id)) {
                result.push({
                  TicketId: t.Id,
                  EventId: event.Id,
                  EventName: event.Title || '',
                  TicketName: t.Name || ''
                })
                remaining.delete(t.Id)
              }
            }
          }
        } catch {
          // ignore per-event fetch errors
        }
      }

      if (result.length) {
        setSelectedTicketsList(result)
      }
    }

    fetchAll()
  }, [type, rule, selectedEvents, onRequestTicketsOfEvent])

  const buildPayload = useCallback(
    (newType, newEvents, newTickets, newMin, newMaxPurchases) => {
      const payload = {
        Type: newType,
        RequestedEventIds:
          newType === TICKET_RULE.DID_NOT_BUY_FROM_ANY_EVENT
            ? []
            : newEvents.map((e) => e.Id)
      }
      if (newType === TICKET_RULE.BOUGHT_ANY_TICKET_FROM_EVENT) {
        payload.RequestedTicketIds = newTickets.map((t) => t.TicketId)
        payload.Min = Number(newMin) || 1
        const mp = Number(newMaxPurchases)
        if (!isNaN(mp) && mp > 0) payload.MaxPurchases = mp
      }
      return payload
    },
    []
  )

  const notifyChange = useCallback(
    (newType, newEvents, newTickets, newMin, newMaxPurchases) => {
      onChange(
        buildPayload(newType, newEvents, newTickets, newMin, newMaxPurchases)
      )
    },
    [onChange, buildPayload]
  )

  const handleTypeChange = (e) => {
    const newType = Number(e.target.value)
    setType(newType)
    notifyChange(
      newType,
      selectedEvents,
      selectedTicketsList,
      min,
      maxPurchases
    )
  }

  const openModal = () => {
    setModalTempSelected([...selectedEvents])
    setSearchValue('')
    setOpenEventModal(true)
  }

  const handleConfirmEvents = () => {
    setSelectedEvents(modalTempSelected)
    setOpenEventModal(false)
    // Reset ticket section since events changed
    setTicketEventId('')
    setLocalTickets([])
    setTicketSelected('')
    notifyChange(
      type,
      modalTempSelected,
      selectedTicketsList,
      min,
      maxPurchases
    )
  }

  const toggleModalEvent = (event) => {
    setModalTempSelected((prev) =>
      prev.find((e) => e.Id === event.Id)
        ? prev.filter((e) => e.Id !== event.Id)
        : [...prev, event]
    )
  }

  const handleTicketEventChange = async (eventId) => {
    setTicketEventId(eventId)
    setTicketSelected('')
    setLocalTickets([])
    if (eventId && onRequestTicketsOfEvent) {
      setPendingLocalTickets(true)
      try {
        const tickets = await onRequestTicketsOfEvent(eventId)
        setLocalTickets(tickets || [])
      } finally {
        setPendingLocalTickets(false)
      }
    }
  }

  const handleAddTicket = () => {
    const eventObj = selectedEvents.find((e) => e.Id === ticketEventId)
    const newTicket = {
      TicketId: ticketSelected,
      EventId: eventObj?.Id,
      EventName: eventObj?.Title || '',
      TicketName: ticketNameSelected
    }
    const newList = [...selectedTicketsList, newTicket]
    setSelectedTicketsList(newList)
    setTicketSelected('')
    setTicketNameSelected('')
    notifyChange(type, selectedEvents, newList, min, maxPurchases)
  }

  const handleRemoveTicket = (ticketId) => {
    const newList = selectedTicketsList.filter((t) => t.TicketId !== ticketId)
    setSelectedTicketsList(newList)
    notifyChange(type, selectedEvents, newList, min, maxPurchases)
  }

  const filteredEvents =
    events?.filter(
      (e) =>
        e.Title?.toLowerCase().includes(searchValue.toLowerCase()) ||
        String(e.Id).includes(searchValue.trim())
    ) || []

  const needsEventSelection = type !== TICKET_RULE.DID_NOT_BUY_FROM_ANY_EVENT

  const ruleIsValid =
    (!needsEventSelection || selectedEvents.length > 0) &&
    (type !== TICKET_RULE.BOUGHT_ANY_TICKET_FROM_EVENT ||
      selectedTicketsList.length > 0)

  useEffect(() => {
    onValidChangeRef.current = onValidChange
  })

  useEffect(() => {
    onValidChangeRef.current?.(ruleIsValid)
  }, [ruleIsValid])

  return (
    <div>
      {/* Type selector + optional Remove button */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <FormControl
          variant='outlined'
          size='small'
          className={classes.field}
          style={{ flex: 1 }}>
          <InputLabel>Condición</InputLabel>
          <Select value={type} onChange={handleTypeChange} label='Condición'>
            {Object.entries(RULE_LABELS).map(([val, label]) => (
              <MenuItem
                key={val}
                value={Number(val)}
                disabled={usedTypes.includes(Number(val))}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {onRemove && (
          <IconButton size='small' onClick={onRemove} style={{ marginLeft: 4 }}>
            <Delete fontSize='small' />
          </IconButton>
        )}
      </div>

      {/* Event selection button */}
      {needsEventSelection && (
        <div className={classes.field}>
          <Button
            variant='outlined'
            onClick={openModal}
            fullWidth
            style={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              minHeight: 40
            }}>
            {selectedEvents.length > 0
              ? selectedEvents.map((e) => `(${e.Id}) ${e.Title}`).join(', ')
              : 'Seleccionar eventos...'}
          </Button>
          {selectedEvents.length === 0 && showError && (
            <Typography
              variant='caption'
              style={{
                color: '#f44336',
                display: 'block',
                marginTop: 4
              }}>
              Debe agregar al menos un evento
            </Typography>
          )}
        </div>
      )}

      {/* Event selection modal */}
      <Dialog
        open={openEventModal}
        onClose={() => setOpenEventModal(false)}
        fullWidth
        maxWidth='sm'>
        <DialogTitle>Seleccionar Eventos</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size='small'
            variant='outlined'
            placeholder='Buscar evento...'
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          {pendingEvents ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 16,
                gap: 8
              }}>
              <CircularProgress size={24} />
              <Typography>Cargando eventos...</Typography>
            </div>
          ) : (
            <List
              dense
              style={{
                maxHeight: 380,
                overflowY: 'auto',
                border: '1px solid #ccc',
                borderRadius: 4
              }}>
              {filteredEvents.map((event) => {
                const isSelected = !!modalTempSelected.find(
                  (e) => e.Id === event.Id
                )
                return (
                  <ListItem
                    key={event.Id}
                    button
                    onClick={() => toggleModalEvent(event)}
                    style={{
                      backgroundColor: isSelected ? '#e3f2fd' : 'transparent'
                    }}>
                    <ListItemText primary={`(${event.Id}) ${event.Title}`} />
                    {isSelected && (
                      <Chip size='small' label='Seleccionado' color='primary' />
                    )}
                  </ListItem>
                )
              })}
              {filteredEvents.length === 0 && (
                <ListItem>
                  <ListItemText primary='No se encontraron eventos' />
                </ListItem>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEventModal(false)} color='primary'>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmEvents}
            color='primary'
            variant='contained'>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Type 3: Min / MaxPurchases + ticket picker */}
      {type === TICKET_RULE.BOUGHT_ANY_TICKET_FROM_EVENT && (
        <div>
          <div style={{ display: 'flex' }}>
            <div className={classes.fieldRow}>
              <TextField
                label='Min. de Tickets Incluidos'
                type='number'
                value={min}
                size='small'
                variant='outlined'
                className={classes.field}
                inputProps={{ min: 1, step: 1 }}
                onInput={(e) => {
                  if (Number(e.target.value) <= 0) e.target.value = 1
                }}
                onChange={(e) => {
                  setMin(e.target.value)
                  notifyChange(
                    type,
                    selectedEvents,
                    selectedTicketsList,
                    e.target.value,
                    maxPurchases
                  )
                }}
                fullWidth
                required
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label='Max. a comprar'
                type='number'
                value={maxPurchases}
                size='small'
                variant='outlined'
                className={classes.field}
                inputProps={{ min: 1, step: 1 }}
                onInput={(e) => {
                  if (Number(e.target.value) <= 0) e.target.value = 1
                }}
                onChange={(e) => {
                  setMaxPurchases(e.target.value)
                  notifyChange(
                    type,
                    selectedEvents,
                    selectedTicketsList,
                    min,
                    e.target.value
                  )
                }}
                fullWidth
              />
            </div>
          </div>

          {selectedEvents.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <FormControl
                  variant='outlined'
                  size='small'
                  style={{ flex: 1 }}
                  className={classes.field}>
                  <InputLabel>Evento</InputLabel>
                  <Select
                    value={ticketEventId}
                    onChange={(e) => handleTicketEventChange(e.target.value)}
                    label='Evento'>
                    {selectedEvents.map((e) => (
                      <MenuItem key={e.Id} value={e.Id}>
                        {`(${e.Id}) ${e.Title}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  variant='outlined'
                  size='small'
                  style={{ flex: 1 }}
                  className={classes.field}>
                  <InputLabel>Ticket</InputLabel>
                  <Select
                    value={ticketSelected}
                    onChange={(e) => {
                      const ticket = localTickets.find(
                        (t) => t.Id === e.target.value
                      )
                      setTicketSelected(e.target.value)
                      setTicketNameSelected(ticket?.Name || '')
                    }}
                    disabled={
                      !ticketEventId ||
                      pendingLocalTickets ||
                      localTickets.length === 0
                    }
                    label='Ticket'>
                    {pendingLocalTickets ? (
                      <MenuItem value=''>
                        <CircularProgress
                          size={16}
                          style={{ marginRight: 8 }}
                        />
                        Cargando...
                      </MenuItem>
                    ) : (
                      localTickets.map((t) => (
                        <MenuItem key={t.Id} value={t.Id}>
                          {t.Name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Button
                  variant='contained'
                  size='small'
                  onClick={handleAddTicket}
                  disabled={
                    !ticketSelected ||
                    selectedTicketsList.some(
                      (t) => t.TicketId === ticketSelected
                    )
                  }
                  startIcon={<Add />}
                  style={{ height: 40, marginBottom: 8, whiteSpace: 'nowrap' }}>
                  Agregar
                </Button>
              </div>
              {selectedTicketsList.length > 0 && (
                <List
                  dense
                  style={{
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    maxHeight: 200,
                    overflowY: 'auto',
                    margin: '0 8px 8px'
                  }}>
                  {selectedTicketsList.map((t) => (
                    <ListItem key={t.TicketId}>
                      <ListItemText
                        primary={t.TicketName}
                        secondary={
                          t.EventId
                            ? `(${t.EventId}) ${t.EventName}`
                            : t.EventName
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge='end'
                          size='small'
                          onClick={() => handleRemoveTicket(t.TicketId)}>
                          <Delete fontSize='small' />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          )}
          {selectedTicketsList.length === 0 && showError && (
            <Typography
              variant='caption'
              style={{
                color: '#f44336',
                marginLeft: 8,
                display: 'block',
                marginTop: 4
              }}>
              Debe agregar al menos un ticket para esta condición
            </Typography>
          )}
        </div>
      )}
    </div>
  )
}

export default RuleForm
